import * as React from 'react';
import { useEffect } from 'react';
import {
  Button,
  FormHelperText,
  Grid,
  TextField,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AmendFailuresTable from './AmendFailuresTable';
import { AmendFailure } from '@tff/types/tff/amend-failure-service';
import { useIntl } from 'react-intl';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useAmendFailures } from '../../hooks/use-amend-failures';

export type EnhancedAmendFailureDynamoItem = AmendFailure & { action?: string };

const searchSchema = yup.object().shape({
  recordLocatorToSearch: yup
    .string()
    .test('len', 'Must be exactly 6 characters', val => !val || val.length === 0 || val.length === 6),
  productToSearch: yup.string(),
  archive: yup.boolean().optional(),
});

const AmendFailurePage: React.FC<{ pnr: string }> = ({ pnr }) => {
  const intl = useIntl();
  const { amendFailuresResponse, setAmendFailuresRequest, errorMessage, searchDone } = useAmendFailures();
  const {
    register: registerSearch,
    handleSubmit: handleSearchSubmit,
    formState: { errors: searchErrors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(searchSchema),
    defaultValues: {
      archive: false,
      recordLocatorToSearch: pnr || '',
      productToSearch: '',
    },
  });

  const archiveChecked = watch('archive');

  useEffect(() => {
    if (pnr !== '') {
      setAmendFailuresRequest({ archive: false, product: '', recordLocator: pnr });
    }
  }, []);

  const submitSearchForm = (data: { [x: string]: any }) => {
    const product = data.productToSearch;
    const recordLocator = data.recordLocatorToSearch;
    const archive = data.archive;
    setAmendFailuresRequest({ archive, product, recordLocator });
  };

  const SearchForm: React.FC = () => (
    <Grid container direction="row" justifyContent="space-between">
      <form onSubmit={handleSearchSubmit(submitSearchForm)} style={{ width: '100%' }}>
        <Grid container justifyContent="center" alignItems="center" gap="20px">
          <Grid item xs={2}>
            <FormControl variant="filled" fullWidth>
              <InputLabel id="product-select-label">{intl.formatMessage({ id: 'global.product' })}</InputLabel>
              <Select {...registerSearch('productToSearch')} name="productToSearch" value={watch('productToSearch')}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="atcomres">Atcomres</MenuItem>
                <MenuItem value="atcomnr">Atcomnr</MenuItem>
                <MenuItem value="aircruiser">Aircruiser</MenuItem>
              </Select>
            </FormControl>
            {searchErrors.productToSearch && (
              <FormHelperText className="tff-amend-failure-helper-text">
                {searchErrors.productToSearch?.message?.toString()}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={2}>
            <TextField
              {...registerSearch('recordLocatorToSearch')}
              type="text"
              name="recordLocatorToSearch"
              variant="filled"
              label={intl.formatMessage({ id: 'global.recordLocator' })}
              fullWidth
              inputProps={{ maxLength: 6, style: { textTransform: 'uppercase' } }}
              defaultValue={pnr}
            />
            {searchErrors.recordLocatorToSearch && (
              <FormHelperText className="tff-amend-failure-helper-text">
                {searchErrors.recordLocatorToSearch?.message?.toString()}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={2}>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    {...registerSearch('archive')}
                    checked={archiveChecked}
                    onChange={e => setValue('archive', e.target.checked)}
                  />
                }
                label={intl.formatMessage({ id: 'global.displayArchive' })}
                labelPlacement="end"
                sx={{ '& .MuiFormControlLabel-label': { lineHeight: 'normal' } }}
              />
            </FormGroup>
            {searchErrors.archive && (
              <FormHelperText className="tff-amend-failure-helper-text">
                {searchErrors.archive?.message?.toString()}
              </FormHelperText>
            )}
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" size="large" type="submit" id="submit">
              {intl.formatMessage({ id: 'global.search' })}
            </Button>
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Box>
      </form>
    </Grid>
  );

  const renderTable = () => {
    if (!searchDone) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Typography variant="h6">Please select a product and start search</Typography>
        </Box>
      );
    } else {
      if (!amendFailuresResponse || amendFailuresResponse.length === 0) {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="h6">Sorry, no matching results found</Typography>
          </Box>
        );
      }
      return <AmendFailuresTable data={amendFailuresResponse} />;
    }
  };

  return (
    <div className="tff-search-page-root">
      <SearchForm />
      <div className="tff-amend-failure-table-root">{renderTable()}</div>
    </div>
  );
};

export default AmendFailurePage;
